
import { Injectable } from '@angular/core';
import { SearchInfoDtoOfGuid, SearchInfoDtoOfInt64, SearchOutputDto, ShipmentSearchInfo, ShipmentType } from '@shared/service-proxies/service-proxies';
import { PermissionCheckerService } from 'abp-ng2-module';

export interface HeaderSearchInputData {
    searchText: string;
    groups: SearchGroupModel[];
}

interface SearchGroupModel {
    name: string;
    items: SearchGroupItemModel[];
}

interface SearchGroupItemModel {
    name: string;
    icon: string;
    url?: any[];
}

@Injectable()
export class HeaderSearchInputDataFactoryService {
    constructor(private _permissionCheckerService: PermissionCheckerService) { }

    mapOutput(searchOutput: SearchOutputDto, searchText: string): HeaderSearchInputData {
        const groups: SearchGroupModel[] = [];

        if (searchOutput.shipments.length) {
            groups.push({
                name: 'Shipments',
                items: searchOutput.shipments.map(shipment => this.mapShipment(shipment)),
            });
        }

        if (searchOutput.orders.length) {
            groups.push({
                name: 'Orders',
                items: searchOutput.orders.map(order => this.mapOrder(order)),
            });
        }

        if (searchOutput.inventories.length) {
            groups.push({
                name: 'Items',
                items: searchOutput.inventories.map(item => this.mapItem(item)),
            });
        }

        return {
            searchText,
            groups,
        };
    }

    private mapItem(item: SearchInfoDtoOfGuid) {
        return {
            name: item.name,
            icon: 'icon-Property-3tag',
        };
    }

    private mapOrder(order: SearchInfoDtoOfInt64) {
        return {
            id: order.id,
            name: order.name,
            icon: 'icon-Property-3clipboard-list',
            url: this.mapOrderUrl(order),
        };
    }

    private mapShipment(shipment: ShipmentSearchInfo) {
        return {
            id: shipment.id,
            name: shipment.name,
            icon: this.mapShipmentIcon(shipment),
            url: this.mapShipmentUrl(shipment),
        };
    }

    private mapShipmentIcon(shipment: ShipmentSearchInfo) {
        switch (shipment.type) {
            case ShipmentType.Inbound: return 'icon-Property-3arrow-down-left';
            case ShipmentType.Outbound: return 'icon-Property-3arrow-up-left';
            default: {
                // Ensure no other type exists
                const exhaustiveCheck: never = shipment.type;
                throw new Error(exhaustiveCheck);
            }
        }
    }

    private mapOrderUrl(order: SearchInfoDtoOfInt64): any[] {
        if (this._permissionCheckerService.isGranted('Pages.Tenant.CarrierShipments')) {
            return ['/app/admin/carrier-orders', order.id];
        } else if (this._permissionCheckerService.isGranted('Pages.Tenant.InventoryOwnerShipments')) {
            return ['/app/admin/orders', order.id];
        } else {
            return undefined;
        }
    }

    private mapShipmentUrl(shipment: ShipmentSearchInfo): any[] {
        if (this._permissionCheckerService.isGranted('Pages.Tenant.CarrierShipments')) {
            return ['/app/admin/carrier-shipments', shipment.id];
        } else if (this._permissionCheckerService.isGranted('Pages.Tenant.InventoryOwnerShipments')) {
            return ['/app/admin/shipments', shipment.id];
        } else {
            return undefined;
        }
    }
}
